import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ArrowForwardRounded,
  AutoGraph,
  Bolt,
  DataUsage,
  Groups2,
  Insights,
  Language,
  MenuBook,
  Public,
  ShowChart,
  Storefront,
  Timeline,
} from '@mui/icons-material';
import {
  obtecIndicators,
  obtecObjectives,
  obtecPublications,
  obtecTechnologies,
} from '../data/pesquisaInovacao';
import { obtecPanelPath, obtecPanels, ObtecPanelKey } from '../data/obtecPanels';

const SectionTitle: React.FC<{ children: React.ReactNode; centered?: boolean; width?: number }> = ({
  children,
  centered = false,
  width = 210,
}) => (
  <Typography
    variant="h5"
    sx={{
      fontWeight: 700,
      color: 'primary.main',
      mb: 2,
      position: 'relative',
      pb: 1,
      textAlign: centered ? 'center' : 'left',
      lineHeight: 1.25,
      fontSize: { xs: '1.3rem', sm: '1.45rem', md: '1.5rem' },
      '&::after': {
        content: '""',
        position: 'absolute',
        left: centered ? '50%' : 0,
        transform: centered ? 'translateX(-50%)' : 'none',
        bottom: 0,
        width: { xs: Math.min(width, 240), sm: width },
        height: 3,
        bgcolor: 'secondary.main',
      },
    }}
  >
    {children}
  </Typography>
);

const PanelAccess: React.FC<{ panel: ObtecPanelKey }> = ({ panel }) => {
  const item = obtecPanels[panel];
  const accent = panel === 'economico' ? '#ff9b21' : '#42a5ff';

  return (
    <Button
      component={RouterLink}
      to={obtecPanelPath(panel)}
      aria-label={`${item.action}: ${item.subtitle}`}
      sx={{
        display: 'block',
        width: '100%',
        minHeight: 132,
        p: { xs: 1.7, sm: 2 },
        textAlign: 'left',
        textTransform: 'none',
        color: '#fff',
        border: `1px solid ${alpha(accent, 0.75)}`,
        borderTop: `4px solid ${accent}`,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(5, 15, 43, 0.96), rgba(12, 30, 70, 0.91))',
        boxShadow: '0 14px 32px rgba(1, 9, 29, 0.28)',
        backdropFilter: 'blur(12px)',
        transition: 'transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease',
        '&:hover': {
          background: 'linear-gradient(135deg, rgba(8, 21, 56, 0.98), rgba(17, 45, 95, 0.96))',
          transform: 'translateY(-4px)',
          boxShadow: `0 18px 36px ${alpha(accent, 0.25)}`,
        },
        '&:focus-visible': { outline: `3px solid ${accent}`, outlineOffset: 3 },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          aria-hidden="true"
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            bgcolor: accent,
            WebkitMask: `url("${item.icon}") center / contain no-repeat`,
            mask: `url("${item.icon}") center / contain no-repeat`,
          }}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color: accent, fontSize: { xs: '0.88rem', sm: '0.96rem' }, fontWeight: 800, lineHeight: 1.25 }}>
            {item.label}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.84)', fontSize: '0.75rem', lineHeight: 1.4, mt: 0.3 }}>
            {item.subtitle}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.5, color: '#fff' }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{item.action}</Typography>
        <ArrowForwardRounded fontSize="small" />
      </Stack>
    </Button>
  );
};

const iconMap: Record<string, React.ReactNode> = {
  Diagnosticar: <Insights fontSize="small" />,
  Mapear: <Language fontSize="small" />,
  Desenvolver: <Bolt fontSize="small" />,
  Disponibilizar: <DataUsage fontSize="small" />,
  Promover: <ShowChart fontSize="small" />,
  Impulsionar: <Timeline fontSize="small" />,
  'BIG DATA': <DataUsage fontSize="small" />,
  BLOCKCHAIN: <Timeline fontSize="small" />,
  CIBERSEGURANÇA: <DataUsage fontSize="small" />,
  'COMPUTAÇÃO EM NUVEM': <Language fontSize="small" />,
  'COMPUTAÇÃO QUÂNTICA': <Bolt fontSize="small" />,
  'INTERNET DAS COISAS (IoT)': <Storefront fontSize="small" />,
  'INTELIGÊNCIA ARTIFICIAL': <Insights fontSize="small" />,
  ROBÓTICA: <AutoGraph fontSize="small" />,
  'Presença Digital': <Public fontSize="small" />,
  'Vendas Digitais': <ShowChart fontSize="small" />,
  'Experiência do Cliente': <Groups2 fontSize="small" />,
  'Gestão e Uso de Dados': <DataUsage fontSize="small" />,
  'Operações e Logística': <Storefront fontSize="small" />,
  'Segurança e Governança de Dados': <DataUsage fontSize="small" />,
  'Tecnologias e Inovação': <Bolt fontSize="small" />,
  'Relatórios Técnicos': <MenuBook fontSize="small" />,
  'Diagnósticos Setoriais': <MenuBook fontSize="small" />,
  'Estudos Aplicados': <MenuBook fontSize="small" />,
  'Manual de Boas Práticas': <MenuBook fontSize="small" />,
};

const getIcon = (title: string) => iconMap[title] ?? <Insights fontSize="small" />;

const PesquisaObtec: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ pt: { xs: 3, md: 9 }, pb: { xs: 6, md: 8 }, backgroundColor: '#f7f8fb' }}>
      <Box sx={{ px: { xs: 2, sm: 3, md: 6 }, maxWidth: 1600, mx: 'auto' }}>
        <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center">
          <Grid item xs={12} md={5}>
            <Chip
              label="Observatório de Tecnologias do Comércio"
              color="primary"
              variant="outlined"
              sx={{ mb: 2, height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                lineHeight: 1.12,
                fontSize: { xs: '2.1rem', sm: '2.6rem', md: '3rem' },
              }}
            >
              OBTEC
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mt: { xs: 2, md: 2.5 },
                color: 'primary.main',
                fontWeight: 700,
                fontSize: { xs: '1.35rem', md: '1.5rem' },
              }}
            >
              Sobre o Observatório
            </Typography>
            <Typography sx={{ mt: 1.5, color: 'text.secondary', lineHeight: 1.85, maxWidth: 660, fontSize: { xs: '0.96rem', md: '1rem' } }}>
              O Observatório de Tecnologias do Comércio - OBTEC é uma plataforma de inovação aberta e de pesquisa aplicada,
              criada para mapear, monitorar e analisar a adoção de tecnologias digitais no comércio de Manaus.
            </Typography>
            <Typography sx={{ mt: 1.5, color: 'text.secondary', lineHeight: 1.85, maxWidth: 660, fontSize: { xs: '0.96rem', md: '1rem' } }}>
              A iniciativa tem como finalidade produzir conhecimento estratégico por meio de diagnósticos, indicadores e
              análises que contribuam para o fortalecimento e a competitividade das empresas do comércio de bens, serviços
              e turismo.
            </Typography>
            <Typography sx={{ mt: 1.5, color: 'text.secondary', lineHeight: 1.85, maxWidth: 660, fontSize: { xs: '0.96rem', md: '1rem' } }}>
              O projeto inicia suas atividades no Centro de Manaus, considerando a relevância econômica da região e seu
              papel estratégico no processo de transformação urbana e digital da cidade.
            </Typography>
          </Grid>

          <Grid item xs={12} md={7}>
            <Box
              sx={{
                position: 'relative',
                isolation: 'isolate',
                minHeight: { xs: 630, sm: 520, md: 560 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                p: { xs: 2, sm: 3 },
                overflow: 'hidden',
                borderRadius: { xs: 2.5, md: 3 },
                bgcolor: '#092042',
                boxShadow: '0 24px 55px rgba(8, 34, 72, 0.18)',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  zIndex: -1,
                  inset: 0,
                  background: 'linear-gradient(180deg, rgba(3, 15, 37, 0.28) 0%, transparent 32%, rgba(3, 12, 30, 0.16) 52%, rgba(3, 12, 30, 0.86) 100%)',
                },
              }}
            >
              <Box
                component="img"
                src="/assets/City/Arte-Manaus.png"
                alt="Teatro Amazonas, Ponte Rio Negro e paisagem de Manaus ao entardecer"
                fetchPriority="high"
                sx={{ position: 'absolute', zIndex: -2, inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
              />
              <Box
                component="img"
                src="/assets/Logo/logo-obtec-claro.png"
                alt="OBTEC — Observatório de Tecnologias do Comércio"
                sx={{ alignSelf: 'flex-end', width: { xs: 170, sm: 215, md: 230 }, maxWidth: '48%', height: 'auto', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.45))' }}
              />
              <Box>
                <Typography sx={{ color: '#fff', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', fontSize: '0.76rem', mb: 1.5, textShadow: '0 2px 8px rgba(0,0,0,0.7)' }}>
                  Explore os painéis interativos
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                  <PanelAccess panel="economico" />
                  <PanelAccess panel="digital" />
                </Stack>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 3, md: 6 }, mt: { xs: 5.5, md: 8 } }}>
        <SectionTitle centered width={120}>Objetivos</SectionTitle>
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', maxWidth: 820, mx: 'auto', mb: { xs: 3, md: 4 }, lineHeight: 1.75 }}>
          Ao concentrar suas ações no território do Centro de Manaus, o OBTEC busca gerar inteligência aplicada, alinhada
          às necessidades reais das empresas locais.
        </Typography>

        <Grid container spacing={2.5}>
          {obtecObjectives.map((item) => (
            <Grid item xs={12} md={6} key={item.title}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderLeft: `3px solid ${theme.palette.secondary.main}`,
                  borderColor: alpha(theme.palette.primary.main, 0.08),
                }}
              >
                <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
                  <Stack direction="row" spacing={1.2} alignItems="center">
                    <Box sx={{ color: 'secondary.main', display: 'grid', placeItems: 'center' }}>{getIcon(item.title)}</Box>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                      {item.title}
                    </Typography>
                  </Stack>
                  <Typography sx={{ mt: 1.2, color: 'text.secondary', lineHeight: 1.75 }}>{item.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card
          variant="outlined"
          sx={{
            mt: 3,
            borderColor: alpha(theme.palette.primary.main, 0.1),
            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.06)',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <SectionTitle width={220}>Metodologia do OBTEC</SectionTitle>
            <Typography sx={{ color: 'text.secondary', lineHeight: 1.85 }}>
              A metodologia do OBTEC está fundamentada em uma abordagem quantitativa e analítica, baseada na coleta
              estruturada de dados primários, por meio de pesquisas aplicadas, e dados secundários, a partir de bases
              públicas e institucionais, integrados em um ambiente de inteligência de dados.
            </Typography>
            <Typography sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.85 }}>
              Os dados são tratados por meio de processos de padronização, validação e modelagem estatística, permitindo a
              construção de indicadores sintéticos de maturidade digital, organizados em dimensões analíticas previamente
              definidas.
            </Typography>
            <Typography sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.85 }}>
              A análise é operacionalizada por meio de ferramentas de Business Intelligence, possibilitando a geração de
              painéis interativos, análises territoriais e comparativas que subsidiam a tomada de decisão e o monitoramento
              contínuo da transformação digital no comércio.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Box id="tecnologias-observadas" component="section" sx={{ px: { xs: 2, sm: 3, md: 6 }, mt: { xs: 5.5, md: 8 }, scrollMarginTop: 96 }}>
        <SectionTitle centered width={270}>Tecnologias Observadas</SectionTitle>
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', maxWidth: 880, mx: 'auto', mb: 2, lineHeight: 1.85 }}>
          O OBTEC monitora um conjunto estratégico de tecnologias emergentes e consolidadas que impactam diretamente a
          transformação digital do setor comercial de Manaus.
        </Typography>
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', maxWidth: 880, mx: 'auto', mb: 4, lineHeight: 1.85 }}>
          Essas tecnologias são acompanhadas para identificar tendências, aplicações práticas e níveis de adoção pelas
          empresas, contribuindo para a geração de inteligência estratégica, inovação e soluções alinhadas às demandas do
          comércio.
        </Typography>

        <Grid container spacing={2.5}>
          {obtecTechnologies.map((item) => (
            <Grid item xs={12} md={6} key={item.title}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderTop: `4px solid ${theme.palette.secondary.main}`,
                  borderColor: alpha(theme.palette.primary.main, 0.08),
                  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.04)',
                }}
              >
                <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <Box sx={{ color: 'secondary.main', display: 'grid', placeItems: 'center' }}>{getIcon(item.title)}</Box>
                    <Box>
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="subtitle2" sx={{ color: 'secondary.main', fontWeight: 700 }}>
                        {item.subtitle}
                      </Typography>
                    </Box>
                  </Stack>
                  <Typography sx={{ mt: 1.5, color: 'text.secondary', lineHeight: 1.75 }}>{item.description}</Typography>
                  <Typography sx={{ mt: 1.2, color: 'text.secondary', lineHeight: 1.75 }}>{item.impact}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box
        id="indicadores"
        component="section"
        sx={{
          mt: { xs: 5.5, md: 8 },
          py: { xs: 4.5, md: 6 },
          background: 'linear-gradient(180deg, rgba(255,243,224,0.95) 0%, rgba(255,249,240,1) 100%)',
          scrollMarginTop: 96,
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3, md: 6 } }}>
          <SectionTitle centered width={320}>Indicadores de Maturidade Digital</SectionTitle>
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', maxWidth: 780, mx: 'auto', mb: { xs: 3, md: 4 }, lineHeight: 1.75 }}>
            Os indicadores organizam a leitura do desenvolvimento digital do comércio e apoiam análises comparativas por
            território, segmento e perfil empresarial.
          </Typography>

          <Grid container spacing={2.5}>
            {obtecIndicators.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.title}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    borderColor: alpha(theme.palette.secondary.main, 0.14),
                    backgroundColor: '#fff',
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
                    <Stack direction="row" spacing={1.2} alignItems="flex-start">
                      <Box sx={{ color: 'secondary.main', display: 'grid', placeItems: 'center' }}>{getIcon(item.title)}</Box>
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                        {item.title}
                      </Typography>
                    </Stack>
                    <Typography sx={{ mt: 1.2, color: 'text.secondary', lineHeight: 1.75 }}>{item.description}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      <Box id="publicacoes" component="section" sx={{ px: { xs: 2, sm: 3, md: 6 }, mt: { xs: 5.5, md: 8 }, scrollMarginTop: 96 }}>
        <SectionTitle centered width={150}>Publicações</SectionTitle>
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', maxWidth: 760, mx: 'auto', mb: { xs: 3, md: 4 }, lineHeight: 1.85 }}>
          Acesse conteúdos estratégicos produzidos, com análises, diagnósticos e estudos aplicados que apoiam a tomada de
          decisão no comércio.
        </Typography>

        <Grid container spacing={2.5}>
          {obtecPublications.map((item) => (
            <Grid item xs={12} md={6} key={item.title}>
              <Card
                variant="outlined"
                sx={{
                  height: '100%',
                  borderColor: alpha(theme.palette.primary.main, 0.08),
                }}
              >
                <CardContent sx={{ p: { xs: 2.4, md: 3 } }}>
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <Box sx={{ color: 'primary.main', display: 'grid', placeItems: 'center' }}>{getIcon(item.title)}</Box>
                    <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                      {item.title}
                    </Typography>
                  </Stack>
                  <Typography sx={{ mt: 1.2, color: 'text.secondary', lineHeight: 1.75 }}>{item.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card
          variant="outlined"
          sx={{
            mt: 3,
            borderColor: alpha(theme.palette.primary.main, 0.1),
            backgroundColor: '#fff',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography variant="overline" sx={{ color: 'secondary.main', fontWeight: 800 }}>
              Outros estudos
            </Typography>
            <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700, mt: 0.5 }}>
              Instituto Fecomércio de Pesquisas Empresariais do Amazonas (IFPEAM)
            </Typography>
            <Typography sx={{ color: 'text.secondary', lineHeight: 1.75, mt: 1 }}>
              Consulte também as publicações e estudos produzidos pelo Instituto Fecomércio de Pesquisas Empresariais do
              Amazonas.
            </Typography>
            <Button
              component="a"
              href="https://www.fecomercio-am.org.br/ifpeam"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="primary"
              sx={{ mt: 2, fontWeight: 700, width: { xs: '100%', sm: 'auto' } }}
            >
              Acessar IFPEAM
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default PesquisaObtec;
